<script setup lang="ts">
import { computed, watch } from "vue";

import type { SearchStickiesQuery } from "@/backend/read/BackendReadSession";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import { useBoardStore } from "@/store/board";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useSearchResultsStore } from "@/store/search/searchResults";

import CurrentBoardSearchResults from "./CurrentBoardSearchResults.vue";
import OtherBoardsSearchResults from "./OtherBoardsSearchResults.vue";
import { groupResultsByBoard } from "./groupSearchResults";
import { useBackendSearch } from "./useBackendSearch";

const menuStore = useSearchMenuStore();
const resultStore = useSearchResultsStore();

watch(
  () => resultStore.currentResult,
  (selected) => {
    if (selected) {
      document.getElementById(`result-${selected.id}`)?.scrollIntoView({ block: "nearest" });
    }
  },
);

const searchQuery = computed<SearchStickiesQuery>(() => ({
  text: menuStore.text,
  teams: menuStore.selectedTeams,
  iterations: menuStore.selectedIterations,
  types: menuStore.selectedStickyTypes,
  flags: menuStore.flags,
  statuses: menuStore.selectedStatusItems,
  assignees: menuStore.selectedAssignees,
  reporters: menuStore.selectedReporters,
}));

useBackendSearch(searchQuery, resultStore);

const currentBoardSearchResults = computed(() => {
  const currentBoardItems = resultStore.found.filter(
    (item) => item.boardId === useBoardStore().board?.id,
  );
  return currentBoardItems.length === 0 ? null : groupResultsByBoard(currentBoardItems)[0];
});
</script>

<template>
  <SidePanelContent id="search-results" :title="$t('linkModal.searchResults')">
    <ComplexList v-keyboard-nav.soft-focus.ignore-trigger>
      <ListEmpty
        v-if="resultStore.hasNoResults"
        data-search-results-anchor
        icon="general/search-results"
        tabindex="-1"
        aria-live="polite"
      >
        {{ $t("linkModal.noSearchResults") }}

        <!-- Reset filters button -->
        <template v-if="menuStore.anyFilterCriteria" #after>
          <BaseButton variant="text" color="primary" @click="menuStore.resetCriteria()">
            {{ $t("linkModal.resetFilters") }}
          </BaseButton>
        </template>
      </ListEmpty>

      <!-- Search results -->
      <CurrentBoardSearchResults
        v-if="currentBoardSearchResults"
        class="focusable-search-results"
        tabindex="-1"
        data-search-results-anchor
        :results="currentBoardSearchResults"
        :selected="resultStore.currentResult"
        :aria-label="$t('label.search.currentBoardResults')"
        @select="resultStore.setCurrent"
      />

      <ListEmpty v-if="resultStore.backendSearching" tabindex="-1" aria-live="polite">
        <LoadingIndicator />
      </ListEmpty>

      <OtherBoardsSearchResults
        v-if="resultStore.hasBackendResults"
        class="focusable-search-results"
        data-search-results-anchor
        tabindex="-1"
        :query="searchQuery"
        :results="resultStore.backendResults"
        :aria-label="$t('label.search.otherBoardResults')"
      />
    </ComplexList>
  </SidePanelContent>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/mixins/shadow";
@use "@/styles/side-panel" as *;

#search-results {
  @include side-panel;

  left: 0;

  @include shadow.default;

  .high-contrast-mode & {
    border-right: 2px solid colors-a11y.$divider-color;
  }

  .focusable-search-results {
    &:focus-visible {
      outline: 3px solid colors.$outline;
    }
  }
}
</style>
